/**
 * Step-up 2FA — 고위험 액션 재인증 Sheet (CF Access 기반).
 *
 * 대상 액션 (docs/specs/superadmin.md §1.2):
 *   - Live 승격 (promote_live)
 *   - 사용자 영구 삭제 (delete_user)
 *   - 환불 승인 (approve_refund)
 *   - Panic 일괄 제어 (panic_rollback)
 *
 * 동작:
 *   1) `useStepUp()` 훅으로 `guard(action, onConfirm)` 호출.
 *   2) 세션이 최근 `freshWindowMs` 내에 검증되었으면 즉시 onConfirm 실행.
 *   3) 아니면 vaul BottomSheet 오픈 → 사용자가 "재인증" 클릭 → CF Access `authorize` 플로우.
 *   4) 서버 `/admin/session/verify-step-up` 로 CF Access JWT (+ step-up AMR) 검증 → 성공 시 갱신.
 *
 * 실제 step-up 검증 구현(Worker) 은 Phase 4.5 Mars 담당. 본 모듈은 UI + 인터페이스만 제공.
 */

import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { Drawer } from 'vaul';

import { emitActionEvent } from './anomaly-hook';
import { fetchCfAccessIdentity, getCfAccessJwtFromCookie } from './cf-access';
import { isStepUpAction, type StepUpAction } from './types';

const VERIFY_ENDPOINT = '/admin/session/verify-step-up';
const DEFAULT_FRESH_WINDOW_MS = 5 * 60 * 1000; // 5분

const ACTION_LABEL: Record<StepUpAction, string> = {
  promote_live: 'Live 승격',
  delete_user: '사용자 영구 삭제',
  approve_refund: '환불 승인',
  panic_rollback: 'Panic 일괄 제어 (롤백·격하·일시중지)',
};

function resolveApiBase(): string {
  const base = import.meta.env.VITE_ADMIN_API_URL ?? '';
  return base.replace(/\/+$/, '');
}

interface StepUpContext {
  readonly action: StepUpAction;
  readonly resolve: (verified: boolean) => void;
}

interface StepUpProviderProps {
  readonly children: ReactNode;
  /** 최근 검증 후 재인증 면제 시간. 기본 5분. */
  readonly freshWindowMs?: number;
}

export interface StepUpController {
  /** 고위험 액션을 감싼다. 검증 통과 시 onConfirm() 호출. */
  guard: (action: StepUpAction, onConfirm: () => void | Promise<void>) => Promise<boolean>;
  /** 세션 freshness 수동 리셋 (테스트/로그아웃 시). */
  reset: () => void;
}

let pendingContext: StepUpContext | null = null;
let openFn: ((ctx: StepUpContext) => void) | null = null;
let lastVerifiedAt = 0;

export async function stepUpGuard(
  action: StepUpAction,
  onConfirm: () => void | Promise<void>,
  freshWindowMs: number = DEFAULT_FRESH_WINDOW_MS,
): Promise<boolean> {
  if (!isStepUpAction(action)) {
    console.warn('[Lashpic Admin] stepUpGuard: 지원하지 않는 action', action);
    return false;
  }

  const now = Date.now();
  if (now - lastVerifiedAt < freshWindowMs) {
    await onConfirm();
    return true;
  }

  if (!openFn) {
    console.error('[Lashpic Admin] StepUpProvider 가 마운트되지 않음 — 액션 차단');
    return false;
  }

  const verified = await new Promise<boolean>((resolve) => {
    const ctx: StepUpContext = { action, resolve };
    pendingContext = ctx;
    openFn?.(ctx);
  });

  if (verified) {
    lastVerifiedAt = Date.now();
    await onConfirm();
  }
  return verified;
}

export function StepUpProvider({
  children,
  freshWindowMs: _freshWindowMs = DEFAULT_FRESH_WINDOW_MS,
}: StepUpProviderProps) {
  const [ctx, setCtx] = useState<StepUpContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const resolvedRef = useRef(false);

  if (!openFn) {
    openFn = (next) => {
      resolvedRef.current = false;
      setError(null);
      setCtx(next);
    };
  }

  const close = useCallback((verified: boolean) => {
    if (!resolvedRef.current && pendingContext) {
      resolvedRef.current = true;
      pendingContext.resolve(verified);
      pendingContext = null;
    }
    setCtx(null);
  }, []);

  const handleReauth = useCallback(async () => {
    if (!ctx) return;
    setSubmitting(true);
    setError(null);
    try {
      const apiBase = resolveApiBase();
      const identity = await fetchCfAccessIdentity();
      const jwt = getCfAccessJwtFromCookie();

      if (!apiBase || !identity) {
        setError('서버 설정 또는 인증 정보가 부족합니다');
        return;
      }

      // Phase 4.5: Worker 가 Cf-Access-Jwt-Assertion 헤더로 step-up 검증.
      //   - 브라우저→Worker: cookie 는 same-site 제한으로 자동 첨부되지 않을 수 있어 Bearer 로도 전달.
      //   - Worker 는 JWKS 검증 + amr 에 step-up 요구 방법(webauthn·otp) 포함 여부 확인.
      const response = await fetch(`${apiBase}${VERIFY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        },
        body: JSON.stringify({ action: ctx.action, email: identity.email }),
      });
      if (!response.ok) {
        setError(response.status === 401 ? '재인증 실패' : '검증 실패');
        return;
      }
      await emitActionEvent(identity.user_uuid, ctx.action, { step_up: 'verified' });
      close(true);
    } catch (err) {
      console.error('[Lashpic Admin] step-up 검증 실패:', err);
      setError('네트워크 오류');
    } finally {
      setSubmitting(false);
    }
  }, [ctx, close]);

  const actionLabel = useMemo(() => (ctx ? ACTION_LABEL[ctx.action] : ''), [ctx]);

  return (
    <>
      {children}
      <Drawer.Root
        open={ctx !== null}
        onOpenChange={(open) => {
          if (!open) close(false);
        }}
      >
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 z-50 bg-black/40" />
          <Drawer.Content className="fixed inset-x-0 bottom-0 z-50 mt-24 flex flex-col rounded-t-2xl border-t border-black/10 bg-white p-6 dark:border-white/10 dark:bg-[#2a2a2c] sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:max-w-md sm:rounded-2xl">
            <Drawer.Title className="font-serif text-lg font-semibold">
              재인증 필요
            </Drawer.Title>
            <Drawer.Description className="mt-1 text-[13px] text-slate-600 dark:text-slate-400">
              <strong>{actionLabel}</strong> 은(는) 고위험 액션입니다. Cloudflare Access 의
              2단계 인증(Passkey / OTP) 으로 재인증해 주세요.
            </Drawer.Description>

            {error ? (
              <p role="alert" className="mt-3 text-[12px] text-rose-600">
                {error}
              </p>
            ) : null}

            <div className="mt-5 flex gap-2">
              <button
                type="button"
                onClick={() => close(false)}
                disabled={submitting}
                className="flex-1 rounded-lg border border-black/10 bg-white px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-black/[0.03] disabled:opacity-50 dark:border-white/10 dark:bg-transparent dark:text-slate-200"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleReauth}
                disabled={submitting}
                className="flex-1 rounded-lg bg-[var(--color-admin-accent,#0A84FF)] px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-50"
              >
                {submitting ? '검증 중...' : '재인증'}
              </button>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </>
  );
}

/**
 * 컴포넌트 안에서 step-up 가드 호출.
 */
export function useStepUp(freshWindowMs: number = DEFAULT_FRESH_WINDOW_MS): StepUpController {
  const guard = useCallback<StepUpController['guard']>(
    (action, onConfirm) => stepUpGuard(action, onConfirm, freshWindowMs),
    [freshWindowMs],
  );
  const reset = useCallback(() => {
    lastVerifiedAt = 0;
  }, []);
  return { guard, reset };
}
